import { between, integer } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { TypeCampaignEnum } from '@/types';
import { includesValuesValidator, requiredWithMessage } from '@/helpers/CustomValidator';
import { maxBoxWaitDelayForOnSiteAutomation } from '@/composables/workflows/constants';
import { convertTimeUnitToSeconds } from '@/helpers/helpers';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import Form from './Form.vue';

export interface WaitData {
  event?: string;
  webhook?: number;
  time_unit_select: {
    value: number;
    unit: string; // SECOND, MINUTE, HOUR, DAY, MONTH
  };
}

export function createDefaultData(data: Partial<WaitData> = {}): WaitData {
  const result: WaitData = {
    time_unit_select: data.time_unit_select ?? {
      value: 10,
      unit: 'MINUTE',
    },
  };

  if (data.event !== undefined) {
    result.event = data.event;
  }
  if (data.webhook !== undefined) {
    result.webhook = data.webhook;
  }

  return result;
}

interface ConditionalIncompatibility {
  operatorId: string;
  condition: (operatorData: WaitData) => boolean;
  descriptionKey: string;
}

const Wait: FlowOperatorMetadata<WaitData> = {
  id: 'boxwait',
  kind: 'action',
  label: 'automatedScenarios.actions.wait.shortLabel',
  icon: 'fa-hourglass',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxwait', 'boxtag', 'boxlist',
        'boxvisitpage', 'boxdelaismax',
      ],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxwait', 'boxtag', 'boxlist',
        'boxdisplayfreepopup', 'boxdisplayfreemodel', 'boxvisitpage',
        'boxdelaismax', 'boxchoosebulklist', 'boxaudiencesetup',
      ],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs: () => 1,

  getSubtitle: (data: WaitData, t) => {
    // Handle JSON string format
    let timeUnitSelect = data.time_unit_select as unknown;
    if (typeof timeUnitSelect === 'string') {
      try {
        timeUnitSelect = JSON.parse(timeUnitSelect);
      } catch {
        return null;
      }
    }
    
    const parsed = timeUnitSelect as { value?: number; unit?: string } | null;
    const { value, unit } = parsed || {};
    if (!value || !unit) return null;
    
    // Map unit to translation key (singular/plural)
    const unitKeyMap: Record<string, { singular: string; plural: string }> = {
      SECOND: { singular: 'second', plural: 'seconds' },
      MINUTE: { singular: 'minute', plural: 'minutes' },
      HOUR: { singular: 'hour', plural: 'hours' },
      DAY: { singular: 'day', plural: 'days' },
      MONTH: { singular: 'month', plural: 'month' },
    };
    const keys = unitKeyMap[unit] || { singular: unit.toLowerCase(), plural: unit.toLowerCase() };
    const unitKey = value === 1 ? keys.singular : keys.plural;
    const unitLabel = t(`automatedScenarios.actions.wait.units.${unitKey}`);
    
    return `${value} ${unitLabel}`;
  },

  async validate(data: WaitData): Promise<ValidationResult> {
    const rules = {
      time_unit_select: {
        value: {
          requiredWithMessage: requiredWithMessage(),
          integer,
          between: between(1, 1000),
        },
        unit: {
          requiredWithMessage: requiredWithMessage(),
          includesValuesValidator: includesValuesValidator([
            'SECOND',
            'MINUTE',
            'HOUR',
            'DAY',
            'MONTH',
          ]),
        },
      },
    };

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();

    return {
      success,
      validate: v$,
    };
  },

  canBeDeleted(): boolean {
    return true;
  },
};

// Conditional incompatibilities for on-site automations
export const conditionalIncompatibilities: ConditionalIncompatibility[] = [
  {
    operatorId: 'boxdisplayfreepopup',
    condition: (operatorData: WaitData) => {
      const delayInSeconds = convertTimeUnitToSeconds(operatorData.time_unit_select);
      return delayInSeconds > maxBoxWaitDelayForOnSiteAutomation;
    },
    descriptionKey: 'automatedScenarios.tooltips.conditionalIncompatibility.boxdisplayfreepopup',
  },
  {
    operatorId: 'boxdisplayfreemodel',
    condition: (operatorData: WaitData) => {
      const delayInSeconds = convertTimeUnitToSeconds(operatorData.time_unit_select);
      return delayInSeconds > maxBoxWaitDelayForOnSiteAutomation;
    },
    descriptionKey: 'automatedScenarios.tooltips.conditionalIncompatibility.boxdisplayfreemodel',
  },
];

export default Wait;
